import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Star, Flag, Globe, ArrowRight, ExternalLink, Award, TrendingUp, ThumbsUp, LayoutGrid, BadgePercent } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Container } from '../components/styled/Container';
import { PageLayout } from '../components/styled/PageLayout';
import { fetchSiteByDomain, fetchReviewsByDomain, fetchSponsoredByDomain, submitReview } from '../store/actions/siteActions';
import { sendReport } from '../services/reportServices';
import { useLocale } from '../context/LocaleContext';
import SeoHead from '../components/SeoHead';
import { useT } from '../hooks/useT';
import { sitePathSlug } from '../utils/sitePathSlug';
import { getCategoryLabel } from '../utils/categoryDisplay';
import AdBanner from '../components/ads/AdBanner';
import AdSidebar from '../components/ads/AdSidebar';
import AdCard from '../components/ads/AdCard';
import { bannerAds, sidebarAds, cardAds } from '../data/adData';
import styled from 'styled-components';

const DetailRow = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
`;

const MainCol = styled.div`
  flex: 1;
  min-width: 0;
`;

const SidebarCol = styled.aside`
  display: none;
  @media (min-width: 1024px) {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: 16rem;
    flex-shrink: 0;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin-bottom: 1.5rem;
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Card = styled(motion.div)`
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCard};
  overflow: hidden;
`;

const AccentBar = styled.div`
  height: 6px;
  background: ${({ theme }) => theme.colors.primary};
`;

const CardInner = styled.div`
  padding: 2rem;
  @media (min-width: 768px) {
    padding: 2.5rem;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const LogoLarge = styled.div`
  width: 5rem;
  height: 5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(0, 48, 135, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontClamp['3xl']};
`;

const HeaderInfo = styled.div`
  flex: 1;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SiteTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontClamp['3xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const ScoreBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  background: rgba(234, 179, 8, 0.1);
  color: ${({ theme }) => theme.colors.gold};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-radius: ${({ theme }) => theme.radiusSm};
`;

const DomainLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  margin-top: 0.25rem;
  &:hover {
    text-decoration: underline;
  }
`;

const ShortDescription = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.75rem 0 0 0;
  max-width: 42rem;
  line-height: 1.6;
`;

const ViewSimilarBtn = styled(Link)`
  padding: 0.75rem 1.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 2rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatBox = styled.div`
  padding: 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ $bg }) => $bg};
`;

const StatLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 500;
  margin-bottom: 0.25rem;
  color: ${({ $color }) => $color};
`;

const StatValue = styled.p`
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontClamp.xl};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const TagsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const Tag = styled.span`
  padding: 0.375rem 1rem;
  background: rgba(220, 38, 38, 0.1);
  color: ${({ theme }) => theme.colors.crimson};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 500;
  border-radius: ${({ theme }) => theme.radiusSm};
`;

const SectionHeading = styled.h2`
  font-size: ${({ theme }) => theme.fontClamp.xl};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 2rem 0 0.75rem 0;
  &:first-of-type {
    margin-top: 0;
  }
`;

const LongDescription = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  line-height: 1.7;
  margin: 0;
`;

const FeaturesList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.foreground};
  line-height: 1.8;
`;

const ReviewCard = styled.div`
  padding: 1rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.border || 'rgba(0,0,0,0.06)'};
  &:last-child {
    border-bottom: none;
  }
`;

const ReviewMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const ReviewText = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
  line-height: 1.5;
`;

const ReviewForm = styled.form`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const StarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const StarBtn = styled.button`
  background: none;
  padding: 0;
  color: ${({ theme }) => theme.colors.mutedForeground};
  &:hover,
  &.filled {
    color: ${({ theme }) => theme.colors.gold};
  }
`;

const ReviewTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 0.75rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  resize: vertical;
  outline: none;
  border: 2px solid transparent;
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const ReviewSubmitBtn = styled.button`
  align-self: flex-start;
  padding: 0.5rem 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.crimson};
  color: ${({ theme }) => theme.colors.crimsonForeground};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  &:hover {
    opacity: 0.9;
  }
`;

const SuccessMsg = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: hsl(142, 71%, 45%);
  font-weight: 600;
  margin: 0;
`;

const ReportBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: ${({ theme }) => theme.colors.mutedForeground};
  background: none;
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const ReportForm = styled.form`
  margin-top: 0.75rem;
  padding: 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ReportInput = styled.input`
  padding: 0.5rem 0.75rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  outline: none;
  border: 2px solid transparent;
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const NotFoundWrap = styled.div`
  text-align: center;
  padding: 5rem 0;
`;

const NotFoundIcon = styled.div`
  width: 5rem;
  height: 5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const SubmitCta = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.crimson};
  color: ${({ theme }) => theme.colors.crimsonForeground};
  font-weight: 600;
  &:hover {
    opacity: 0.9;
  }
`;

const SponsoredHeading = styled.h2`
  font-size: ${({ theme }) => theme.fontClamp.xl};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 2.5rem 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SponsoredLabel = styled.span`
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const SponsoredGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const SponsoredCard = styled.a`
  display: block;
  padding: 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard};
  text-decoration: none;
  color: inherit;
  border: 1px solid ${({ theme }) => theme.border || 'rgba(0,0,0,0.06)'};
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: ${({ theme }) => theme.shadowCard}, 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const SponsoredCardType = styled.span`
  display: inline-block;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.2rem 0.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(234, 179, 8, 0.15);
  color: ${({ theme }) => theme.colors.gold};
  margin-bottom: 0.5rem;
`;

const SponsoredCardTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontClamp.base};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0 0 0.25rem 0;
`;

const SponsoredCardDesc = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0;
  line-height: 1.45;
`;

const SponsoredCode = styled.div`
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
  font-family: ui-monospace, monospace;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.foreground};
`;

const SponsoredExpiry = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.5rem 0 0 0;
`;

const SponsoredLinkText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.75rem;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`;

function SiteDetail() {
  const dispatch = useDispatch();
  const { prependPath } = useLocale();
  const t = useT();
  const { slug: siteSlug } = useParams();
  const { site, reviews, sponsored, loading, error } = useSelector((state) => state.sites);
  const reportEmail = useSelector((state) => state.auth.user?.email || '');

  const [reviewRating, setReviewRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const [reportMessage, setReportMessage] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');

  useEffect(() => {
    if (!siteSlug) return;
    dispatch(fetchSiteByDomain(siteSlug));
    dispatch(fetchReviewsByDomain(siteSlug));
    dispatch(fetchSponsoredByDomain(siteSlug));
  }, [dispatch, siteSlug]);

  const allReviews = reviews;
  const siteDetails = site; // alias for longDescription/features block (avoid no-undef on 'details')

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (reviewRating < 1) return;
    const result = await dispatch(submitReview(siteSlug, { rating: reviewRating, text: reviewText }));
    if (result?.success) {
      setReviewRating(0);
      setReviewText('');
      setReviewSubmitted(true);
    }
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    setReportLoading(true);
    setReportError('');
    try {
      await sendReport({
        domain: site?.domain || siteSlug,
        message: reportMessage,
        email: reportEmail,
      });
      setReportSubmitted(true);
      setReportMessage('');
      setReportOpen(false);
    } catch (err) {
      setReportError(err.response?.data?.message || err.message || 'Failed to send report');
    } finally {
      setReportLoading(false);
    }
  };

  if (loading && !site) {
    return (
      <PageLayout>
        <SeoHead title={`${siteSlug} | CiteMatch`} />
        <Navbar />
        <Container style={{ paddingTop: '5rem', paddingBottom: '5rem' }}>
          <p style={{ color: 'var(--muted-foreground)' }}>Loading site…</p>
        </Container>
        <Footer />
      </PageLayout>
    );
  }

  if (!site) {
    return (
      <PageLayout>
        <SeoHead title={`Site not found | CiteMatch`} noindex />
        <Navbar />
        <Container style={{ paddingTop: '5rem', paddingBottom: '5rem' }}>
          <NotFoundWrap>
            <NotFoundIcon>
              <Globe size={32} />
            </NotFoundIcon>
            <h1 style={{ margin: 0, fontSize: '1.875rem' }}>Site not found</h1>
            <p style={{ color: 'var(--muted-foreground)', marginTop: '0.5rem' }}>
              {error ? error : `"${siteSlug}" is not in our database yet.`}
            </p>
            <SubmitCta to={prependPath('/submit')}>Submit this site <ArrowRight size={14} /></SubmitCta>
          </NotFoundWrap>
        </Container>
        <Footer />
      </PageLayout>
    );
  }

  const detailSeo = site?.seo || {};
  const pageTitle = detailSeo.title ? String(detailSeo.title) : `${site.title} (${site.domain}) | CiteMatch`;
  const pageDescription =
    (detailSeo.description && String(detailSeo.description)) ||
    (site?.description ? String(site.description).replace(/\s+/g, ' ').trim().slice(0, 160) : undefined);
  const seoKeywords = detailSeo.keywords ? String(detailSeo.keywords) : undefined;
  const detailNoindex = detailSeo.robots && String(detailSeo.robots).toLowerCase().includes('noindex');
  const canon = detailSeo.canonicalPath;
  const detailCanonical =
    canon && String(canon).trim()
      ? prependPath(String(canon).trim().startsWith('/') ? String(canon).trim() : `/${String(canon).trim()}`)
      : undefined;

  return (
    <PageLayout>
      <SeoHead
        title={pageTitle}
        description={pageDescription}
        keywords={seoKeywords}
        canonicalPath={detailCanonical}
        noindex={detailNoindex}
      />
      <Navbar />
      <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
        <BackLink to={prependPath('/')}><ArrowLeft size={16} strokeWidth={2.5} /> {t('common.back')}</BackLink>

        <DetailRow>
          <MainCol>
        <Card initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <AccentBar />
          <CardInner>
            <HeaderRow>
              <LogoLarge>{site.title.charAt(0)}</LogoLarge>
              <HeaderInfo>
                <TitleRow>
                  <SiteTitle>{site.title}</SiteTitle>
                  <ScoreBadge><Star size={12} fill="currentColor" /> {(site.similarityScore / 10).toFixed(1)}</ScoreBadge>
                </TitleRow>
                <DomainLink href={`https://${site.domain}`} target="_blank" rel="noopener noreferrer">
                  {site.domain} <ExternalLink size={12} />
                </DomainLink>
                <ShortDescription>{site.description}</ShortDescription>
              </HeaderInfo>
              <ViewSimilarBtn to={prependPath(`/similar/${sitePathSlug(site)}`)}>
                {t('common.viewSimilarSites')} <ArrowRight size={14} />
              </ViewSimilarBtn>
            </HeaderRow>

            <StatsGrid>
              {site.alternativeRank != null && site.alternativeTo && (
                <StatBox $bg="rgba(0,48,135,0.05)">
                  <StatLabel $color="var(--primary)"><Award size={14} strokeWidth={2.5} /> Alternative Rank</StatLabel>
                  <StatValue>#{site.alternativeRank} to {site.alternativeTo}</StatValue>
                </StatBox>
              )}
              {site.trending && (
                <StatBox $bg="rgba(34,197,94,0.08)">
                  <StatLabel $color="hsl(142,71%,45%)"><TrendingUp size={14} strokeWidth={2.5} /> Momentum</StatLabel>
                  <StatValue style={{ fontSize: 'inherit' }}>{site.trending}</StatValue>
                </StatBox>
              )}
              {site.userScore != null && (
                <StatBox $bg="rgba(234,179,8,0.05)">
                  <StatLabel $color="var(--gold)"><ThumbsUp size={14} strokeWidth={2.5} /> User Score</StatLabel>
                  <StatValue>{site.userScore}/5</StatValue>
                </StatBox>
              )}
              <StatBox $bg="rgba(128,0,0,0.05)">
                <StatLabel $color="var(--maroon)"><LayoutGrid size={14} strokeWidth={2.5} /> Category</StatLabel>
                <StatValue style={{ fontSize: '1.25rem', fontWeight: 600 }}>{site.categoryName || getCategoryLabel(site.category)}</StatValue>
              </StatBox>
            </StatsGrid>

            <TagsWrap>
              {site.tags.map((tag) => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </TagsWrap>
          </CardInner>
        </Card>

        <div style={{ margin: '1.5rem 0' }}>
          <AdBanner ad={bannerAds[0]} variant="inline" />
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <AdCard ad={cardAds[0]} />
        </div>

        {(site?.longDescription || (site?.features && site.features.length > 0)) && (
          <Card initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ marginTop: '2rem' }}>
            <AccentBar />
            <CardInner>
              {siteDetails?.longDescription && (
                <>
                  <SectionHeading style={{ marginTop: 0 }}>About this site</SectionHeading>
                  <LongDescription>{site.longDescription}</LongDescription>
                </>
              )}
              {site?.features && site.features.length > 0 && (
                <>
                  <SectionHeading>Features</SectionHeading>
                  <FeaturesList>
                    {site.features.map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </FeaturesList>
                </>
              )}
            </CardInner>
          </Card>
        )}

        <Card initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ marginTop: '2rem' }}>
          <AccentBar />
          <CardInner>
            <SectionHeading style={{ marginTop: 0 }}>Reviews</SectionHeading>
            {reviewSubmitted && <SuccessMsg>Thanks! Your review has been added.</SuccessMsg>}
            <ReviewForm onSubmit={handleReviewSubmit}>
              <StarRow>
                {[1, 2, 3, 4, 5].map((n) => (
                  <StarBtn
                    key={n}
                    type="button"
                    className={reviewRating >= n ? 'filled' : ''}
                    onClick={() => setReviewRating(n)}
                    aria-label={`${n} star`}
                  >
                    <Star size={24} fill={reviewRating >= n ? 'currentColor' : 'none'} />
                  </StarBtn>
                ))}
              </StarRow>
              <ReviewTextarea
                placeholder="Your review (optional)"
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
              />
              <ReviewSubmitBtn type="submit">Submit review</ReviewSubmitBtn>
            </ReviewForm>
            {allReviews.length > 0 && (
              <div style={{ marginTop: '1.5rem' }}>
                {allReviews.map((r) => (
                  <ReviewCard key={r.id}>
                    <ReviewMeta>
                      <Star size={12} fill="currentColor" style={{ color: 'var(--gold)' }} />
                      {r.rating}/5 · {r.author} · {r.date}
                    </ReviewMeta>
                    <ReviewText>{r.text}</ReviewText>
                  </ReviewCard>
                ))}
              </div>
            )}
          </CardInner>
        </Card>

        <div style={{ marginTop: '1.5rem' }}>
          <ReportBtn type="button" onClick={() => setReportOpen((o) => !o)}>
            <Flag size={14} /> Report wrong info
          </ReportBtn>
          {reportOpen && (
            <ReportForm onSubmit={handleReportSubmit}>
              {reportError && <SuccessMsg style={{ color: 'hsl(0, 84%, 60%)' }}>{reportError}</SuccessMsg>}
              <ReportInput
                as="textarea"
                placeholder="What's wrong? (optional)"
                value={reportMessage}
                onChange={(e) => setReportMessage(e.target.value)}
                rows={3}
              />
              <ReviewSubmitBtn type="submit" disabled={reportLoading}>Send report</ReviewSubmitBtn>
            </ReportForm>
          )}
          {reportSubmitted && <SuccessMsg>Report received. Thanks for helping us improve.</SuccessMsg>}
        </div>

        {sponsored.length > 0 && (
          <>
            <SponsoredHeading>
              <BadgePercent size={22} />
              Deals & offers
            </SponsoredHeading>
            <SponsoredLabel>Sponsored</SponsoredLabel>
            <SponsoredGrid>
              {sponsored.map((item) => (
                <SponsoredCard key={item.id} href={item.link || '#'} target="_blank" rel="noopener noreferrer">
                  <SponsoredCardType>{item.type === 'coupon' ? 'Coupon' : 'Deal'}</SponsoredCardType>
                  <SponsoredCardTitle>{item.title}</SponsoredCardTitle>
                  <SponsoredCardDesc>{item.description}</SponsoredCardDesc>
                  {item.code && <SponsoredCode>{item.code}</SponsoredCode>}
                  {item.expiry && <SponsoredExpiry>Expires: {item.expiry}</SponsoredExpiry>}
                  <SponsoredLinkText>Get offer <ArrowRight size={14} /></SponsoredLinkText>
                </SponsoredCard>
              ))}
            </SponsoredGrid>
          </>
        )}
          </MainCol>
          <SidebarCol aria-label="Sponsored">
            {sidebarAds.map((ad) => (
              <AdSidebar key={ad.id} ad={ad} />
            ))}
          </SidebarCol>
        </DetailRow>
      </Container>
      <Footer />
    </PageLayout>
  );
}

export default SiteDetail;
